"use client";

import type { CSSProperties } from "react";

import { cn } from "@/lib/utils";

import { VS_FONT_FEATURES } from "./constants";
import {
  ALL_LOGOS,
  ALL_SPEAKERS,
  CHAPTERS,
  RECAP_VIDEO,
  VENUE_BATCH_LDN,
  VENUE_NY,
} from "./data";
import { VsEmailCapture } from "./email-capture";
import { VsGallery } from "./gallery";
import { VsHero } from "./hero";
import { VsHighlights } from "./highlights";
import { VsLogos } from "./logos";
import { VsNav } from "./nav";
import { PageRail, PageRailSection } from "./page-rail";
import { VsPanels } from "./panels";
import { VsRecapVideo } from "./recap-video";
import { VsSpeakers } from "./speakers";
import { VsTestimonials } from "./testimonials";
import { VsVenues } from "./venue";

/**
 * Visionaries Summit landing page. Standalone — no global Nav/SiteFooter.
 *
 * Retrospective page — both summits (Batch LDN, London and KidSuper Studios,
 * Brooklyn) have now happened, so there's no active registration on the
 * page: the nav's Apply CTA that used to link out to Luma now scrolls down
 * to the register-interest form instead, the Venues section (VsVenues)
 * links out to each venue's own site instead of an Apply banner, and the
 * hero CTAs point at the on-page Panels and Speakers sections.
 *
 * The `--vs-action-h` CSS variable is set on the page wrapper (NOT `<html>`)
 * via a Tailwind class so it cascades into every descendant — page-root
 * padding-top reserves it, and the nav uses it as `top` so it sits flush
 * below it. There's no announcement bar on this page, so it's 0.
 *
 * Layout order:
 *
 * 1. VsNav  · 2. VsHero
 * 3. VsLogos (combined London + New York brands)
 * 4. PageRail with chapter nav + sections:
 *    Speakers (id="speakers" — ALL_SPEAKERS, the combined London + New York
 *    roster, mixed rather than grouped by city; each card carries a
 *    location badge) · Panels (id="panels" — video grid of the London
 *    panel recordings, LONDON_PANELS; a second New York grid only appears
 *    once NY_PANELS has entries) · "Previously" (id="london" — "What
 *    happens at a Visionaries Summit": recap video, a combined Clips +
 *    Gallery block, and testimonials, all folded under one flat chapter —
 *    see CHAPTERS in data.ts; Voices/testimonials no longer has its own
 *    sidebar entry) · Venues (id="venue" — VsVenues, both cities' venues
 *    side by side, each linking to its own site)
 * 5. VsEmailCapture (Register) — full-bleed footer band, outside the 1440px
 *    rail column so it reads as a closing footer rather than another section
 *
 * Sections after the hero are wrapped in a 1440px max-width column so the page
 * stops growing once the design reference does (page bg #0d0d0d fills the
 * gutters on wider viewports). The hero is intentionally full-bleed.
 */
export function VisionariesSummitPage() {
  const pageVars: CSSProperties = {
    ["--vs-rail-top" as string]: "108px",
    paddingTop: "var(--vs-action-h, 36px)",
  };

  return (
    <div
      style={pageVars}
      data-vs-page-root
      className={cn(cnPageRoot, "[--vs-action-h:0px]")}
    >
      {/* Full-bleed hero. Nav CTA scrolls down to the register-interest
          form instead of linking out to Luma. */}
      <div className="relative">
        <VsNav applyHref="#register" applyLabel="Register interest" />
        <VsHero />
      </div>

      {/* Brand logos — sit directly below the hero CTAs. Negative top margin
          pulls the strip up to tighten the gap below the CTAs. */}
      <div className="mx-auto -mt-12 w-full max-w-[1440px] max-md:-mt-8">
        <VsLogos logos={ALL_LOGOS} label="The brands who joined us." />
        {/* Stats ticker ("3 days · 9 panels · 30 founders") hidden for now —
            restore by re-adding <VsStats /> (component kept in ./stats). */}
      </div>

      {/* Full-bleed divider between the brand logos and the page rail —
          extends past the 1440px column on either side so it reads as a
          continuous horizontal rule. */}
      <div aria-hidden className="h-px w-full bg-[#313335]" />

      <div className="mx-auto w-full max-w-[1440px]">
        <PageRail chapters={CHAPTERS}>
          <PageRailSection withBottomBorder>
            <div className="px-8 max-md:px-6">
              <VsSpeakers
                id="speakers"
                title="Previous Speakers."
                speakers={ALL_SPEAKERS}
                compact
              />
            </div>
          </PageRailSection>

          <PageRailSection withBottomBorder>
            <div className="px-8 max-md:px-6">
              <VsPanels />
            </div>
          </PageRailSection>

          {/* "Previously" chapter — a single flat sidebar entry (no more
              folded sub-items) covering the whole recap: recap video, a
              combined Clips + Gallery block under one "Gallery" heading, and
              testimonials — all blended in, none with their own sidebar
              entry. Previous speakers now live in the merged "Speakers"
              section above, not here. */}
          <PageRailSection withBottomBorder id="london">
            <div
              style={{ fontFeatureSettings: VS_FONT_FEATURES }}
              className="scroll-mt-[120px] px-8 pt-[72px] max-md:px-6 max-md:pt-12"
            >
              <h2 className="mb-10 text-[40px] leading-[1.2] font-normal tracking-[-1.6px] text-white max-md:mb-8 max-md:text-[32px] max-md:tracking-[-1.28px]">
                What you can expect.
              </h2>

              <VsRecapVideo
                playbackId={RECAP_VIDEO.playbackId}
                src={RECAP_VIDEO.src}
                poster={RECAP_VIDEO.poster}
                className="mx-auto max-w-[1000px]"
              />

              <div
                id="gallery"
                style={{ fontFeatureSettings: VS_FONT_FEATURES }}
                className="mt-[72px] max-md:mt-12"
              >
                <p className="mb-3 text-[12px] tracking-[0.0967em] text-[#95999d] uppercase">
                  gallery
                </p>
                <h2 className="mb-12 text-[40px] leading-[1.2] font-normal tracking-[-1.6px] text-white max-md:mb-9 max-md:text-[32px] max-md:tracking-[-1.28px]">
                  Snippets from the summits.
                </h2>
                <VsHighlights hideHeading />
                <VsGallery hideHeading className="mt-10" />
              </div>

              <VsTestimonials className="pb-12 max-md:pb-8" />
            </div>
          </PageRailSection>

          {/* Venues — both cities' venues shown side by side, each linking
              to its own site, instead of a single-venue Apply promo. */}
          <PageRailSection withBottomBorder={false}>
            <div className="px-8 max-md:px-6">
              <VsVenues
                venues={[VENUE_NY, VENUE_BATCH_LDN]}
                className="pt-12 max-md:pt-8"
                title="Spaces chosen to spark conversation."
              />
            </div>
          </PageRailSection>
        </PageRail>
      </div>

      {/* Register — full-bleed footer band. Deliberately sits outside the
          1440px rail column (unlike every section above it) so it reads as a
          closing footer rather than another chapter, with its own border-top
          seam and full-width background. */}
      <div className="w-full border-t border-[#313335] bg-[#0d0d0d]">
        <div className="mx-auto w-full max-w-[1440px] px-8 max-md:px-6">
          <VsEmailCapture />
        </div>
      </div>
    </div>
  );
}

// Tailwind only sees full class names, so combining the page bg + font stack +
// the reduced-motion-aware padding-top transition into a single literal helps
// JIT pick everything up. Keeps the wrapper JSX readable.
const cnPageRoot =
  "bg-[#0d0d0d] font-[Inter,ui-sans-serif,system-ui,sans-serif] text-white" +
  " transition-[padding-top] duration-200 ease-out motion-reduce:transition-none";
